import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LocationContext = createContext(null);

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};

export const LocationProvider = ({ children }) => {
  const [locations, setLocations] = useState([]);
  const [segments, setSegments] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch locations and segments on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [locRes, segRes] = await Promise.all([
          axios.get(`${API}/locations`),
          axios.get(`${API}/segments`)
        ]);
        setLocations(locRes.data);
        setSegments(segRes.data);
        
        // Default: select all non-HQ locations
        setSelectedLocations(locRes.data.filter(loc => loc.code !== 'OPTSC_HQ').map(loc => loc.id));
      } catch (error) {
        console.error('Error fetching locations:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Select all locations
  const selectAll = () => {
    setSelectedLocations(locations.map(loc => loc.id));
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedLocations([]);
  };

  // Toggle single location
  const toggleLocation = (locationId) => {
    setSelectedLocations(prev => {
      if (prev.includes(locationId)) {
        return prev.filter(id => id !== locationId);
      }
      return [...prev, locationId];
    });
  };

  // Select locations by segment
  const selectSegment = (segmentId) => {
    const segment = segments.find(s => s.id === segmentId);
    if (segment) {
      setSelectedLocations(segment.location_ids || []);
    }
  };

  // Get selected location names
  const getSelectedLocationNames = () => {
    const activeLocations = locations.filter(l => l.code !== 'OPTSC_HQ');
    if (selectedLocations.length === activeLocations.length && activeLocations.length > 0) {
      return 'All Locations';
    }
    if (selectedLocations.length === 0) {
      return 'No Locations';
    }
    // Check if selection matches a franchise
    for (const seg of segments) {
      const segIds = new Set(seg.location_ids || []);
      const selIds = new Set(selectedLocations);
      if (segIds.size === selIds.size && [...segIds].every(id => selIds.has(id))) {
        return seg.name;
      }
    }
    if (selectedLocations.length === 1) {
      const loc = locations.find(l => l.id === selectedLocations[0]);
      return loc?.name || 'Unknown';
    }
    return `${selectedLocations.length} Locations`;
  };

  // Get location IDs for API calls
  const getLocationIds = () => {
    return selectedLocations.length > 0 ? selectedLocations : locations.map(l => l.id);
  };

  // Check if a segment matches current selection
  const isSegmentSelected = (segmentId) => {
    const segment = segments.find(s => s.id === segmentId);
    if (!segment) return false;
    
    const segmentIds = new Set(segment.location_ids || []);
    const selectedIds = new Set(selectedLocations);
    
    if (segmentIds.size !== selectedIds.size) return false;
    for (const id of segmentIds) {
      if (!selectedIds.has(id)) return false;
    }
    return true;
  };

  const value = {
    locations,
    segments,
    selectedLocations,
    setSelectedLocations,
    loading,
    selectAll,
    clearSelection,
    toggleLocation,
    selectSegment,
    getSelectedLocationNames,
    getLocationIds,
    isSegmentSelected
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};

export default LocationContext;
